import { useState } from "react";
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  Users as UsersIcon,
  UtensilsCrossed,
} from "lucide-react";
import type { Activity, ActionItem } from "@/api/types";
import { ACTIVITY_ROLES, roleVar } from "@/lib/colors";
import { eur, fmtDayShort } from "@/lib/format";
import ActionItems from "./ActionItems";

interface Props {
  activity: Activity;
  items: ActionItem[];
  canEdit: boolean;
  currentUserId: string;
  todayStr: string;
  onMutated: () => void;
  defaultOpen?: boolean;
}

const SOURCE_LABELS: Record<string, string> = { gmail: "Gmail", gcal: "Calendar" };

const ACTIVITY_ICONS: Record<Activity["type"], typeof MessageSquare> = {
  note: MessageSquare,
  call: Phone,
  email: Mail,
  meeting: UsersIcon,
  hospitality: UtensilsCrossed,
  status_change: Calendar,
};

const TYPE_LABELS: Record<Activity["type"], string> = {
  note: "Note",
  call: "Call",
  email: "Email",
  meeting: "Meeting",
  hospitality: "Hospitality",
  status_change: "Stage change",
};

const NEEDS_ATTENTION_TYPES = new Set(["meeting", "call", "hospitality"]);

/** Add 1 day to a YYYY-MM-DD string, returning a YYYY-MM-DD string. */
function addOneDay(dateStr: string): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

/** One interaction on a timeline rail (place inside an `<li class="relative pl-7">`). */
export default function ActivityCard({
  activity: a,
  items,
  canEdit,
  currentUserId,
  todayStr,
  onMutated,
  defaultOpen = false,
}: Props) {
  const [open, setOpen] = useState(defaultOpen);

  const Icon = ACTIVITY_ICONS[a.type] ?? MessageSquare;
  const typeRole = ACTIVITY_ROLES[a.type] ?? "cyan";
  const isUpcoming = a.type === "meeting" && a.date > todayStr;
  const detail = a.detail;

  const title =
    detail?.title || (a.text ? a.text.split("\n")[0].slice(0, 80) : TYPE_LABELS[a.type]);

  // Action-item progress badge
  const total = items.length;
  const done = items.filter((i) => i.status === "done").length;
  const openItems = items.filter((i) => i.status !== "done");
  const tomorrow = addOneDay(todayStr);
  const anyPastDue = openItems.some((i) => i.due_date != null && i.due_date < todayStr);
  const anyDueSoon = openItems.some(
    (i) => i.due_date != null && i.due_date >= todayStr && i.due_date <= tomorrow,
  );
  const badgeRole = anyPastDue
    ? "pink"
    : anyDueSoon
      ? "amber"
      : total > 0 && done === total
        ? "green"
        : "violet";

  const showAddItemsChip = total === 0 && NEEDS_ATTENTION_TYPES.has(a.type) && a.date <= todayStr;

  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <>
      {/* Rail node, tinted by activity type */}
      <span
        className="absolute left-0 -translate-x-1/2 top-2.5 grid h-6 w-6 place-items-center rounded-full ring-4 ring-ink-950"
        style={{ ...roleVar(typeRole), background: "rgb(var(--c) / 0.14)", color: "rgb(var(--c))" }}
      >
        <Icon className="w-3 h-3" />
      </span>

      <div
        className={`panel-tight p-3 ${isUpcoming ? "ring-1 ring-accent-azure/30" : showAddItemsChip ? "ring-1 ring-accent-amber/40" : ""}`}
      >
        <button
          type="button"
          className="w-full flex items-center gap-2 text-sm text-mut-500 text-left"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          <Chevron className="w-3.5 h-3.5 shrink-0" />
          <span className="chip-tint" style={roleVar(typeRole)}>
            {TYPE_LABELS[a.type]}
          </span>
          <span>{fmtDayShort(a.date)}</span>
          {a.type === "hospitality" && detail?.cost_amount != null && (
            <span>· {eur(detail.cost_amount)}</span>
          )}
          {a.author && <span className="truncate">· {a.author}</span>}
          <span className="ml-auto flex items-center gap-1.5">
            {total > 0 && (
              <span className="chip-tint" style={roleVar(badgeRole)}>
                {done}/{total} done
              </span>
            )}
            {showAddItemsChip && (
              <span className="chip-tint" style={roleVar("amber")}>
                Add next steps
              </span>
            )}
            {isUpcoming && (
              <span className="chip-tint" style={roleVar("azure")}>
                Upcoming
              </span>
            )}
            {a.source && SOURCE_LABELS[a.source] && (
              <span className="chip bg-fg/[0.05] text-mut-500">{SOURCE_LABELS[a.source]}</span>
            )}
          </span>
        </button>

        {!open && <div className="mt-1.5 text-md truncate text-mut-100">{title}</div>}

        {open && (
          <div className="mt-2 space-y-1.5 text-md">
            {detail?.location && (
              <div className="flex items-center gap-1.5 text-mut-300">
                <MapPin className="w-3.5 h-3.5" /> {detail.location}
              </div>
            )}
            {detail?.participants && detail.participants.length > 0 && (
              <div className="flex items-center gap-1.5 text-mut-300">
                <UsersIcon className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">
                  {detail.participants.map((p) => p.name || p.email).join(", ")}
                </span>
              </div>
            )}
            {a.text && <div className="whitespace-pre-wrap">{a.text}</div>}
            {detail?.body && (
              <div className="max-h-48 overflow-y-auto whitespace-pre-wrap text-sm text-mut-300 rounded-lg bg-ink-800 p-2.5">
                {detail.body}
              </div>
            )}
            <ActionItems
              dealId={a.deal_id}
              parentActivityId={a.id}
              items={items}
              canEdit={canEdit}
              currentUserId={currentUserId}
              onMutated={onMutated}
              onAdded={() => setOpen(true)}
            />
          </div>
        )}
      </div>
    </>
  );
}
